import React from "react";
import { 
  ShieldCheck, 
  Award, 
  Users, 
  Clock, 
  CheckCircle, 
  Sparkles,
  Leaf,
  ThumbsUp,
  TrendingUp,
  Star,
  Home,
  Building,
  Truck,
  Target,
  Heart
} from "lucide-react";
import { Link } from "react-router-dom";

const AboutSection = () => {
  const features = [
    {
      icon: <ShieldCheck className="text-blue-600" size={24} />,
      title: "Licensed & Insured",
      description: "Fully insured with $2M liability coverage for your peace of mind"
    },
    {
      icon: <Clock className="text-indigo-600" size={24} />,
      title: "Same Day Service",
      description: "Emergency cleaning available with quick response times"
    },
    {
      icon: <Leaf className="text-green-600" size={24} />,
      title: "Eco-Friendly",
      description: "Green cleaning products that are safe for families and pets"
    },
    {
      icon: <Award className="text-amber-600" size={24} />,
      title: "5-Star Rated",
      description: "Consistently excellent reviews from our satisfied customers"
    }
  ];

  const stats = [
    { number: "15+", label: "Years Experience", icon: <TrendingUp className="text-blue-500" size={20} /> },
    { number: "25k+", label: "Happy Customers", icon: <Users className="text-indigo-500" size={20} /> },
    { number: "98%", label: "Satisfaction Rate", icon: <ThumbsUp className="text-green-500" size={20} /> },
    { number: "24/7", label: "Emergency Service", icon: <Clock className="text-amber-500" size={20} /> }
  ];

  const services = [
    { name: "Residential Cleaning", count: "12,500+" },
    { name: "Commercial Cleaning", count: "2,800+" },
    { name: "Emergency Restoration", count: "1,200+" },
    { name: "Carpet Cleaning", count: "8,900+" }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-blue-50">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <Sparkles size={16} />
            TRUSTED SINCE 2008
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Melbourne's Most <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-700">Trusted</span> Cleaning Service
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            At Knight Cleaning, we've been transforming homes and businesses with premium cleaning solutions 
            for over 15 years. Our commitment to excellence has made us Melbourne's preferred cleaning partner.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Image and Stats */}
          <div>
            <div className="relative">
              {/* Main Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1583947581924-860bda6a26df?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=500&q=80" 
                  alt="Professional Cleaning Team"
                  className="w-full h-[500px] object-cover hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                
                {/* Floating Card 1 */}
                <div className="absolute top-6 left-6 bg-white p-4 rounded-xl shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="bg-green-100 p-2 rounded-lg">
                      <CheckCircle className="text-green-600" size={20} />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">Same Day</p>
                      <p className="text-sm text-gray-600">Service Available</p>
                    </div>
                  </div>
                </div>

                {/* Floating Card 2 */}
                <div className="absolute bottom-6 right-6 bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-4 rounded-xl shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 p-2 rounded-lg">
                      <Award size={20} />
                    </div>
                    <div>
                      <p className="font-bold">Award Winning</p>
                      <p className="text-sm text-blue-100">2023 Service Excellence</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats Bar */}
              <div className="relative -mt-12 mx-4">
                <div className="bg-white rounded-xl shadow-xl p-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    {stats.map((stat, index) => (
                      <div key={index} className="text-center">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          {stat.icon}
                          <div className="text-3xl font-bold text-gray-900">{stat.number}</div>
                        </div>
                        <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Content */}
          <div>
            <div className="mb-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Why Thousands of Melbourne Families & Businesses Choose Us
              </h3>
              <p className="text-gray-600 mb-6">
                With over 15 years of experience, Knight Cleaning has established itself as a leader 
                in professional cleaning services. Our trained technicians use state-of-the-art equipment 
                and eco-friendly products to deliver exceptional results every time.
              </p>
              <p className="text-gray-600 mb-8">
                We believe in transparent pricing, honest communication, and going above and beyond 
                to ensure complete customer satisfaction. Your trust is our most valuable asset.
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="bg-white p-5 rounded-xl border border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-3 rounded-lg group-hover:scale-110 transition-transform duration-300">
                      {feature.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">{feature.title}</h4>
                      <p className="text-sm text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Services Summary */}
            <div className="mb-10">
              <h4 className="text-xl font-bold text-gray-900 mb-4">Our Cleaning Expertise</h4>
              <div className="space-y-3">
                {services.map((service, index) => (
                  <div key={index} className="flex items-center justify-between p-3 hover:bg-blue-50 rounded-lg transition-colors group">
                    <div className="flex items-center gap-3">
                      <div className="h-2 w-2 bg-blue-600 rounded-full group-hover:scale-150 transition-transform"></div>
                      <span className="font-medium text-gray-800 group-hover:text-blue-600 transition-colors">{service.name}</span>
                    </div>
                    <span className="font-bold text-blue-700 group-hover:scale-110 transition-transform">{service.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Mission & Vision */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-5 rounded-xl border border-blue-200">
                <div className="flex items-center gap-3 mb-3">
                  <Target className="text-blue-600" size={20} />
                  <h4 className="font-bold text-blue-700">Our Mission</h4>
                </div>
                <p className="text-sm text-gray-700">
                  To enhance lives through exceptional cleaning services that create healthier environments.
                </p>
              </div>
              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-5 rounded-xl border border-indigo-200">
                <div className="flex items-center gap-3 mb-3">
                  <Heart className="text-indigo-600" size={20} />
                  <h4 className="font-bold text-indigo-700">Our Vision</h4>
                </div>
                <p className="text-sm text-gray-700">
                  To be Melbourne's most trusted name in sustainable, premium cleaning services.
                </p>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/about"
                className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 text-center"
              >
                Learn More About Us
              </Link>
              <Link
                to="/contact"
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-semibold px-8 py-4 rounded-full transition-all duration-300 text-center"
              >
                Get Free Quote
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="mt-10 pt-8 border-t border-gray-200">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="text-amber-400 fill-amber-400" size={18} />
                    ))}
                  </div>
                  <span className="font-bold text-gray-900">4.9/5</span>
                  <span className="text-gray-600 text-sm">(250+ reviews)</span>
                </div>
                <div className="h-4 w-px bg-gray-300 hidden md:block"></div>
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-600 font-medium">Trusted by:</div>
                  <div className="flex items-center gap-3">
                    <Home className="text-blue-500" size={20} />
                    <Building className="text-indigo-500" size={20} />
                    <Truck className="text-green-500" size={20} />
                    <span className="text-gray-700 font-semibold">+500 Local Businesses</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Commitment Banner */}
        <div className="mt-20 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-8 md:p-12 text-white overflow-hidden relative">
          <div 
            className="absolute right-0 top-0 bottom-0 w-1/3 bg-cover bg-center opacity-10"
            style={{
              backgroundImage: 'url("https://images.unsplash.com/photo-1583947581924-860bda6a26df?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80")'
            }}
          ></div>
          
          <div className="relative grid grid-cols-1 gap-8 items-center">
            <div className="lg:col-span-2">
              <h3 className="text-3xl font-bold mb-4">
                Our Commitment to Excellence
              </h3>
              <p className="text-blue-100 text-lg">
                Every cleaning project is backed by our 100% satisfaction guarantee. 
                If you're not completely happy with our service, we'll return and make it right – no questions asked.
              </p>
            </div>
            <br />
            <div className="flex justify-center">
               <p className="text-blue-100 text-lg">
                We proudly serve all of Victoria including Melbourne and surrounding areas, delivering top-tier cleaning solutions
                to homes and businesses alike. Experience the Knight Cleaning difference today!
              </p>
              <br />
               <p className="text-blue-100 text-lg">
                For Emergency work, we are available 24/7 to respond to your needs promptly and efficiently.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;